import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
// Importar contexto do AgentHub (opcional)
import { useAgentHubContext } from '../AgentHub/contexts/AgentHubContext';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Table,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { formatDistance } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import {
  getAutomationById,
  toggleAutomationActive,
  duplicateAutomation,
} from "./services/AutomationServices";
import {
  getAutomationRules,
  toggleAutomationRuleActive,
  deleteAutomationRule,
} from "./services/AutomationServices";
import AutomationRuleForm from "./AutomationRuleForm";
import { getPlanLabel } from "../../utils/PlanUtils";
import {
  IconArrowLeft,
  IconPlus,
  IconPencil,
  IconTrash,
  IconPower,
  IconCopy,
} from "@tabler/icons-react";
import TimelineView from "./TimelineView";
import AutomationStats from "./AutomationStats";
import MessageHistory from "./MessageHistory";
import styled from "styled-components";

const AutomationDetail = () => {
  const { id, agentId } = useParams();
  const navigate = useNavigate();
  
  // Detectar contexto do AgentHub
  const agentContext = useAgentHubContext();
  const contextAgentId = agentContext?.agentId || agentId;
  const isInsideAgentHub = Boolean(agentContext) || window.location.pathname.includes('/agent-hub/');
  const [automation, setAutomation] = useState(null);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const fetchAutomationDetails = async () => {
    setLoading(true);
    try {
      const data = await getAutomationById(id);
      setAutomation(data);

      // Buscar regras
      // Incluir contextAgentId se estivermos dentro do AgentHub
      const rulesData = await getAutomationRules(id, contextAgentId);
      setRules(rulesData.results);
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao buscar os detalhes da automação.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => {
        if (isInsideAgentHub) {
          navigate(`/agent-hub/${contextAgentId}/automations`);
        } else {
          navigate("/automations");
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAutomationDetails();
  }, [id, navigate]);

  const handleToggleActive = async () => {
    try {
      const response = await toggleAutomationActive(id);
      setAutomation({
        ...automation,
        active: response.active,
      });
      Swal.fire({
        title: "Sucesso!",
        text: `Automação ${
          response.active ? "ativada" : "desativada"
        } com sucesso!`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao alterar o status da automação.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDuplicate = async () => {
    try {
      await duplicateAutomation(id);
      Swal.fire({
        title: "Duplicado!",
        text: "Automação duplicada com sucesso.",
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        if (isInsideAgentHub) {
          navigate(`/agent-hub/${contextAgentId}/automations`);
        } else {
          navigate("/automations");
        }
      });
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao duplicar a automação.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleToggleRuleActive = async (ruleId) => {
    try {
      const response = await toggleAutomationRuleActive(id, ruleId);
      setRules(
        rules.map((rule) =>
          rule.id === ruleId ? { ...rule, active: response.active } : rule
        )
      );
      Swal.fire({
        title: "Sucesso!",
        text: `Regra ${
          response.active ? "ativada" : "desativada"
        } com sucesso!`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        title: "Erro!",
        text: "Ocorreu um erro ao alterar o status da regra.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleDeleteRule = (ruleId) => {
    Swal.fire({
      title: "Tem certeza?",
      text: "Deseja realmente excluir esta regra? Esta ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, excluir!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteAutomationRule(id, ruleId);
          setRules(rules.filter((rule) => rule.id !== ruleId));
          Swal.fire({
            title: "Excluído!",
            text: "Regra excluída com sucesso.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        } catch (error) {
          Swal.fire({
            title: "Erro!",
            text: "Ocorreu um erro ao excluir a regra.",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const handleAddRule = () => {
    setEditingRule(null);
    setShowRuleForm(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setShowRuleForm(true);
  };

  const handleRuleSaved = () => {
    setShowRuleForm(false);
    setEditingRule(null);
    fetchAutomationDetails();
  };

  const getAccountTypeLabel = (type) => {
    const types = {
      clinic: "Clínica",
      real_estate: "Imobiliária",
      law_firm: "Advocacia",
      generic: "Genérico",
      other: "Outro",
    };
    return types[type] || type;
  };

  const getTemplateActionLabel = (action) => {
    const actions = {
      confirmation_message: "Confirmação",
      tips_message: "Dicas",
      feedback: "Feedback",
      reminder_message: "Lembrete",
      generic: "Genérico",
    };
    return actions[action] || action;
  };

  const getTimeUnitLabel = (unit) => {
    const units = {
      minutes: "minutos",
      hours: "horas",
      days: "dias",
      weeks: "semanas",
    };
    return units[unit] || unit;
  };

  if (loading) {
    return (
      <Container className="">
        <div className="text-center p-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Carregando detalhes da automação...</p>
        </div>
      </Container>
    );
  }

  if (!automation) {
    return (
      <Container fluid>
        <Alert variant="danger">
          Automação não encontrada ou você não tem permissão para acessá-la.
        </Alert>
      </Container>
    );
  }

  return (
    <ContainerWrapper className="">
      {showRuleForm ? (
        <AutomationRuleForm />
      ) : (
        <>
          <Row className="mb-4">
            <Col>
              <h3>{automation.name}</h3>
              <p className="text-muted">{automation.description}</p>
            </Col>
            <Col xs="auto" className="d-flex align-items-start gap-2">
              <Button
                variant={
                  automation.active ? "outline-danger" : "outline-success"
                }
                onClick={handleToggleActive}
                className="d-flex align-items-center"
              >
                <IconPower size={18} className="me-1" />
                {automation.active ? "Desativar" : "Ativar"}
              </Button>
              <Button
                variant="outline-success"
                onClick={handleDuplicate}
                className="d-flex align-items-center"
              >
                <IconCopy size={18} className="me-1" />
                Duplicar
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => {
                  if (isInsideAgentHub) {
                    navigate(`/agent-hub/${contextAgentId}/automations/edit/${id}`);
                  } else {
                    navigate(`/automations/edit/${id}`);
                  }
                }}
                className="d-flex align-items-center"
              >
                <IconPencil size={18} className="me-1" />
                Editar
              </Button>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={12}>
              <AutomationStats automationId={id} />
            </Col>
          </Row>
          <Row className="mb-4">
            <Col md={4}>
              <Card>
                <Card.Body>
                  <h5 className="mb-3">Informações</h5>
                  <dl className="row mb-0">
                    <dt className="col-5">Status:</dt>
                    <dd className="col-7">
                      <Badge bg={automation.active ? "success" : "danger"}>
                        {automation.active ? "Ativo" : "Inativo"}
                      </Badge>
                    </dd>

                    <dt className="col-5">Tipo de Negócio:</dt>
                    <dd className="col-7">
                      {getAccountTypeLabel(getPlanLabel(automation.plan_id))}
                    </dd>

                    <dt className="col-5">Criado em:</dt>
                    <dd className="col-7">
                      {automation.created_at &&
                        new Date(automation.created_at).toLocaleString()}
                    </dd>

                    <dt className="col-5">Atualizado:</dt>
                    <dd className="col-7">
                      {automation.updated_at &&
                        formatDistance(
                          new Date(automation.updated_at),
                          new Date(),
                          {
                            addSuffix: true,
                            locale: pt,
                          }
                        )}
                    </dd>

                    <dt className="col-5">Total de Regras:</dt>
                    <dd className="col-7">{rules.length}</dd>
                  </dl>
                </Card.Body>
              </Card>
            </Col>

            <Col md={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Regras de Automação</h5>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddRule}
                    className="d-flex align-items-center"
                  >
                    <IconPlus size={16} className="me-1" />
                    Adicionar Regra
                  </Button>
                </Card.Header>
                <Card.Body>
                  {rules.length === 0 ? (
                    <Alert variant="info">
                      Nenhuma regra cadastrada. Adicione regras para definir
                      quando as mensagens serão enviadas.
                    </Alert>
                  ) : (
                    <Table responsive hover>
                      <thead>
                        <tr>
                          <th>Tipo</th>
                          <th>Template</th>
                          <th>Quando Enviar</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((rule) => (
                          <tr key={rule.id}>
                            <td>
                              {getTemplateActionLabel(rule.template_action)}
                            </td>
                            <td>{rule.template_name || "Template Padrão"}</td>
                            <td>
                              {rule.time_value}{" "}
                              {getTimeUnitLabel(rule.time_unit)}{" "}
                              {rule.direction === "before" ? "antes" : "depois"}{" "}
                              {rule.template_action === "feedback"
                                ? "do final"
                                : "do início"}
                            </td>
                            <td>
                              <Badge bg={rule.active ? "success" : "secondary"}>
                                {rule.active ? "Ativo" : "Inativo"}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex gap-1">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  title={rule.active ? "Desativar" : "Ativar"}
                                  onClick={() =>
                                    handleToggleRuleActive(rule.id)
                                  }
                                >
                                  <IconPower size={16} />
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  title="Editar"
                                  onClick={() => handleEditRule(rule)}
                                >
                                  <IconPencil size={16} />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  title="Excluir"
                                  onClick={() => handleDeleteRule(rule.id)}
                                >
                                  <IconTrash size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <TimelineView rules={rules} />
            <Row className="mb-4">
              <Col md={12}>
                <MessageHistory automationId={id} />
              </Col>
            </Row>
          </Row>
        </>
      )}
    </ContainerWrapper>
  );
};

const ContainerWrapper = styled.div`
  margin: auto;
  overflow-x: hidden;
  @media (max-width: 768px) {
    width: 100%;
    margin-top: unset;
  }
`;

export default AutomationDetail;
